import type { Command } from '@/core/Command';
import { cloneTag, type SemanticTag } from '@/semantics/SemanticTag';
import type { TagStore } from '@/semantics/TagStore';

/**
 * Undo records for semantic tags.
 *
 * Tags go on the same history as planting, ground strokes, and the wand, so one Ctrl+Z walks back
 * through a session in the order it happened rather than through two timelines that interleave
 * unpredictably. A tag is an edit to the document — it changes what a render will produce — so it
 * belongs on that timeline and not in a separate "annotations" undo stack.
 *
 * Every record snapshots the tag on construction. `TagStore` copies on the way in and out, but the
 * caller may still be holding the object it passed, and an undo record that aliased live state would
 * restore whatever the tag has since become instead of what it was.
 */

export class AddTagCommand implements Command {
  readonly label: string;
  readonly kind = 'tag' as const;
  private readonly tag: SemanticTag;

  constructor(
    private readonly store: TagStore,
    tag: SemanticTag,
    label = 'Add tag',
  ) {
    this.tag = cloneTag(tag);
    this.label = label;
  }

  execute(): void {
    this.store.add(cloneTag(this.tag));
  }

  undo(): void {
    this.store.remove(this.tag.id);
  }
}

export class RemoveTagsCommand implements Command {
  readonly label: string;
  readonly kind = 'remove' as const;
  private readonly tags: SemanticTag[];

  constructor(
    private readonly store: TagStore,
    tags: readonly SemanticTag[],
    label?: string,
  ) {
    this.tags = tags.map(cloneTag);
    this.label = label ?? (tags.length === 1 ? 'Remove tag' : `Remove ${tags.length} tags`);
  }

  execute(): void {
    for (const tag of this.tags) this.store.remove(tag.id);
  }

  undo(): void {
    for (const tag of this.tags) this.store.add(cloneTag(tag));
  }
}

/**
 * Edit a tag in place.
 *
 * `mergeKey` follows the same convention the inspector's sliders use: consecutive edits sharing a key
 * collapse into one history entry, so dragging a location tag's radius is one undo rather than forty.
 * Typing in the label field uses it too — a tag's text should come back in one press, not letter by
 * letter.
 */
export class UpdateTagCommand implements Command {
  readonly label: string;
  readonly kind = 'tag' as const;
  readonly mergeKey?: string;

  private readonly before: SemanticTag;
  private after: SemanticTag;

  constructor(
    private readonly store: TagStore,
    before: SemanticTag,
    after: SemanticTag,
    label = 'Edit tag',
    mergeKey?: string,
  ) {
    this.before = cloneTag(before);
    this.after = cloneTag(after);
    this.label = label;
    // Assigned only when present: under `exactOptionalPropertyTypes` an explicit undefined is not the
    // same as an absent optional, and the history reads absence as "never merge".
    if (mergeKey !== undefined) this.mergeKey = mergeKey;
  }

  execute(): void {
    this.store.add(cloneTag(this.after));
  }

  undo(): void {
    this.store.add(cloneTag(this.before));
  }

  /**
   * Absorb a later edit to the same tag, keeping this record's original "before".
   *
   * Guarded on the id as well as the merge key: two tags edited in turn under one key are two edits,
   * and folding them together would make undo restore the wrong record.
   */
  mergeWith(next: Command): boolean {
    if (!(next instanceof UpdateTagCommand)) return false;
    if (next.mergeKey === undefined || next.mergeKey !== this.mergeKey) return false;
    if (next.before.id !== this.before.id) return false;
    this.after = cloneTag(next.after);
    return true;
  }
}
